import { useState, useEffect, useRef, useCallback, useMemo } from 'react';
import type { CustomerInfo, CountryOption } from '../types';
import type { ResearchModeState } from '../types';

interface YouViewProps {
  // Contact form props
  countries: CountryOption[];
  defaultCountry: string;
  existingCustomer: Partial<CustomerInfo> | null;
  privacyPolicyUrl?: string;
  onCustomerSubmit: (customer: Partial<CustomerInfo>) => void;

  // Email verification props (now part of contact form)
  emailVerified: boolean;
  verificationPending: boolean;
  onVerifyEmail: () => void;
  onConfirmOtp: (code: string) => void;
  onResendOtp: () => void;

  // Research mode props
  showResearchMode?: boolean;
  researchModeState: ResearchModeState;
  onToggleResearchMode: (enabled: boolean) => void;
  onUpdateNotificationPrefs: (prefs: { browser: boolean; email: boolean }) => void;
}

type SavedField = 'name' | 'country' | 'phone' | 'phonePrefix' | null;

export function YouView({
  countries,
  defaultCountry,
  existingCustomer,
  privacyPolicyUrl,
  onCustomerSubmit,
  emailVerified,
  verificationPending,
  onVerifyEmail,
  onConfirmOtp,
  onResendOtp,
  showResearchMode = true,
  researchModeState,
  onToggleResearchMode,
  onUpdateNotificationPrefs,
}: YouViewProps) {
  // Contact form state
  const [email, setEmail] = useState(existingCustomer?.email || '');
  const [name, setName] = useState(existingCustomer?.name || '');
  const [phone, setPhone] = useState(existingCustomer?.phone || '');
  const [country, setCountry] = useState(existingCustomer?.country || defaultCountry);
  const [phonePrefix, setPhonePrefix] = useState(() => {
    // Initialize phone prefix from existing customer or default country
    const defaultCountryObj = countries.find(c => c.code === (existingCustomer?.country || defaultCountry));
    return defaultCountryObj?.phonePrefix || '+1';
  });
  const [privacyAccepted, setPrivacyAccepted] = useState(true);

  // Email editing state
  const [isEditingEmail, setIsEditingEmail] = useState(!emailVerified && !existingCustomer?.email);
  const [isVerifying, setIsVerifying] = useState(false);

  // OTP state
  const [otpCode, setOtpCode] = useState('');

  // Country autocomplete state
  const [countrySearch, setCountrySearch] = useState('');
  const [showCountrySuggestions, setShowCountrySuggestions] = useState(false);
  const countryInputRef = useRef<HTMLInputElement>(null);

  // Phone prefix dropdown state
  const [showPrefixDropdown, setShowPrefixDropdown] = useState(false);
  const [prefixSearch, setPrefixSearch] = useState('');
  const prefixDropdownRef = useRef<HTMLDivElement>(null);

  // Auto-save feedback state
  const [savedField, setSavedField] = useState<SavedField>(null);
  const savedTimeoutRef = useRef<ReturnType<typeof setTimeout> | null>(null);

  // Track original values to detect changes
  const originalValues = useRef({
    name: existingCustomer?.name || '',
    phone: existingCustomer?.phone || '',
    country: existingCustomer?.country || defaultCountry,
    phonePrefix: countries.find(c => c.code === (existingCustomer?.country || defaultCountry))?.phonePrefix || '+1',
  });

  // Get current country display name
  const selectedCountry = countries.find((c) => c.code === country);
  const countryDisplayName = selectedCountry?.name || country;

  // Filter countries for autocomplete
  const filteredCountries = useMemo(() => {
    if (!countrySearch) return countries;
    const search = countrySearch.toLowerCase();
    return countries.filter(c =>
      c.name.toLowerCase().includes(search) ||
      c.code.toLowerCase().includes(search)
    );
  }, [countries, countrySearch]);

  // Filter prefixes for dropdown
  const filteredPrefixes = useMemo(() => {
    if (!prefixSearch) return countries;
    const search = prefixSearch.toLowerCase();
    return countries.filter(c =>
      c.phonePrefix?.includes(search) ||
      c.name.toLowerCase().includes(search) ||
      c.code.toLowerCase().includes(search)
    );
  }, [countries, prefixSearch]);

  // Show saved feedback briefly
  const showSavedFeedback = useCallback((field: SavedField) => {
    if (savedTimeoutRef.current) {
      clearTimeout(savedTimeoutRef.current);
    }
    setSavedField(field);
    savedTimeoutRef.current = setTimeout(() => {
      setSavedField(null);
    }, 2000);
  }, []);

  // Cleanup timeout on unmount
  useEffect(() => {
    return () => {
      if (savedTimeoutRef.current) {
        clearTimeout(savedTimeoutRef.current);
      }
    };
  }, []);

  // Close dropdowns when clicking outside
  useEffect(() => {
    const handleClickOutside = (e: MouseEvent) => {
      if (countryInputRef.current && !countryInputRef.current.contains(e.target as Node)) {
        setShowCountrySuggestions(false);
      }
      if (prefixDropdownRef.current && !prefixDropdownRef.current.contains(e.target as Node)) {
        setShowPrefixDropdown(false);
        setPrefixSearch('');
      }
    };
    document.addEventListener('mousedown', handleClickOutside);
    return () => document.removeEventListener('mousedown', handleClickOutside);
  }, []);

  // Reset verifying state when verification pending changes
  useEffect(() => {
    if (verificationPending) {
      setIsVerifying(false);
    }
  }, [verificationPending]);

  // Auto-save handler for individual fields
  const handleFieldSave = useCallback((field: 'name' | 'phone' | 'country' | 'phonePrefix', value: string) => {
    const originalValue = originalValues.current[field];
    if (value !== originalValue) {
      originalValues.current[field] = value;
      onCustomerSubmit({
        email: email || undefined,
        name: field === 'name' ? value : name || undefined,
        phone: field === 'phone' ? value : phone || undefined,
        country: field === 'country' ? value : country,
      });
      showSavedFeedback(field);
    }
  }, [email, name, phone, country, onCustomerSubmit, showSavedFeedback]);

  // Handle email submit for verification
  const handleEmailSubmit = () => {
    if (email && privacyAccepted && !isVerifying) {
      setIsVerifying(true);
      onCustomerSubmit({ email });
      onVerifyEmail();
    }
  };

  // Handle change email click
  const handleChangeEmail = () => {
    setIsEditingEmail(true);
  };

  // Handle country selection from autocomplete
  const handleCountrySelect = (countryCode: string) => {
    setCountry(countryCode);
    setCountrySearch('');
    setShowCountrySuggestions(false);
    handleFieldSave('country', countryCode);
  };

  // Handle phone prefix selection
  const handlePrefixSelect = (prefix: string) => {
    setPhonePrefix(prefix);
    setShowPrefixDropdown(false);
    setPrefixSearch('');
    handleFieldSave('phonePrefix', prefix);
  };

  // Render saved indicator
  const SavedIndicator = ({ field }: { field: SavedField }) => (
    <span
      className={`text-green-600 text-xs transition-opacity duration-300 ${
        savedField === field ? 'opacity-100' : 'opacity-0'
      }`}
    >
      ✓
    </span>
  );

  return (
    <div className="h-full overflow-y-auto bg-white">
      <div className="p-4 space-y-6">
        {/* Section 1: Contact Information */}
        <div>
          <h3 className="text-sm font-semibold text-gray-900 mb-3 flex items-center gap-2">
            <span>📧</span> Contact Information
          </h3>
          <div className="space-y-3">
            {/* Email Field with Inline Verification */}
            <div>
              <label className="flex items-center justify-between text-xs font-medium text-gray-700 mb-1">
                <span>Email *</span>
                {emailVerified && (
                  <span className="text-green-600 flex items-center gap-1">
                    <span>✓</span> Verified
                  </span>
                )}
              </label>

              {/* Email input or display */}
              {isEditingEmail || (!emailVerified && !verificationPending) ? (
                <div className="flex gap-2">
                  <input
                    type="email"
                    value={email}
                    onChange={(e) => setEmail(e.target.value)}
                    className="flex-1 px-3 py-2 text-sm border border-gray-300 rounded focus:outline-none focus:ring-2 focus:ring-blue-500"
                    placeholder="your.email@example.com"
                    disabled={isVerifying}
                  />
                  <button
                    onClick={handleEmailSubmit}
                    disabled={!email || !privacyAccepted || isVerifying}
                    className={`px-3 py-2 text-sm font-medium text-white rounded whitespace-nowrap transition-colors ${
                      isVerifying
                        ? 'bg-blue-400 cursor-wait'
                        : 'bg-blue-600 hover:bg-blue-700 disabled:bg-gray-300 disabled:cursor-not-allowed'
                    }`}
                  >
                    {isVerifying ? 'Sending...' : 'Verify'}
                  </button>
                </div>
              ) : emailVerified ? (
                <div className="flex items-center justify-between">
                  <span className="px-3 py-2 text-sm text-gray-700 bg-gray-50 border border-gray-200 rounded flex-1">
                    {email}
                  </span>
                  <button
                    onClick={handleChangeEmail}
                    className="ml-2 text-xs text-blue-600 hover:underline"
                  >
                    Change
                  </button>
                </div>
              ) : null}

              {/* OTP Input - shows when verification pending */}
              {verificationPending && (
                <div className="mt-2 p-3 bg-blue-50 border border-blue-200 rounded">
                  <div className="text-xs text-gray-600 mb-2">
                    Verification code sent to <strong>{email}</strong>
                  </div>
                  <div className="flex gap-2">
                    <input
                      type="text"
                      value={otpCode}
                      onChange={(e) => setOtpCode(e.target.value.replace(/\D/g, ''))}
                      placeholder="6-digit code"
                      maxLength={6}
                      className="flex-1 px-3 py-2 text-sm border border-gray-300 rounded focus:outline-none focus:ring-2 focus:ring-blue-500 text-center tracking-widest"
                    />
                    <button
                      onClick={() => {
                        onConfirmOtp(otpCode);
                        setOtpCode('');
                      }}
                      disabled={otpCode.length !== 6}
                      className="px-4 py-2 text-sm font-medium text-white bg-blue-600 rounded hover:bg-blue-700 disabled:bg-gray-300 disabled:cursor-not-allowed"
                    >
                      Confirm
                    </button>
                  </div>
                  <button
                    onClick={onResendOtp}
                    className="mt-2 text-xs text-gray-500 hover:text-gray-700"
                  >
                    Resend code
                  </button>
                </div>
              )}
            </div>

            {/* Name Field - auto-save on blur */}
            <div>
              <label className="flex items-center justify-between text-xs font-medium text-gray-700 mb-1">
                <span>Name</span>
                <SavedIndicator field="name" />
              </label>
              <input
                type="text"
                value={name}
                onChange={(e) => setName(e.target.value)}
                onBlur={() => handleFieldSave('name', name)}
                className="w-full px-3 py-2 text-sm border border-gray-300 rounded focus:outline-none focus:ring-2 focus:ring-blue-500"
                placeholder="John Doe"
              />
            </div>

            {/* Country Field - autocomplete with free entry */}
            <div ref={countryInputRef} className="relative">
              <label className="flex items-center justify-between text-xs font-medium text-gray-700 mb-1">
                <span>Country</span>
                <SavedIndicator field="country" />
              </label>
              <input
                type="text"
                value={showCountrySuggestions ? countrySearch : countryDisplayName}
                onChange={(e) => {
                  setCountrySearch(e.target.value);
                  setShowCountrySuggestions(true);
                }}
                onFocus={() => {
                  setShowCountrySuggestions(true);
                  setCountrySearch('');
                }}
                onBlur={() => {
                  // Allow free entry: if user typed something and closes dropdown, save it
                  setTimeout(() => {
                    if (countrySearch && !showCountrySuggestions) {
                      // Save the free text entry as country name (use as code too)
                      setCountry(countrySearch);
                      handleFieldSave('country', countrySearch);
                    }
                  }, 150);
                }}
                className="w-full px-3 py-2 text-sm border border-gray-300 rounded focus:outline-none focus:ring-2 focus:ring-blue-500"
                placeholder="Type or search country..."
              />
              {/* Country suggestions dropdown */}
              {showCountrySuggestions && (
                <div className="absolute z-50 w-full mt-1 bg-white border border-gray-200 rounded-lg shadow-lg max-h-48 overflow-y-auto">
                  {filteredCountries.length > 0 ? (
                    filteredCountries.map((c) => (
                      <button
                        key={c.code}
                        onClick={() => handleCountrySelect(c.code)}
                        className={`w-full px-3 py-2 text-left text-sm hover:bg-gray-50 ${
                          c.code === country ? 'bg-blue-50 text-blue-700' : 'text-gray-700'
                        }`}
                      >
                        {c.name}
                      </button>
                    ))
                  ) : countrySearch ? (
                    <button
                      onClick={() => {
                        setCountry(countrySearch);
                        setShowCountrySuggestions(false);
                        handleFieldSave('country', countrySearch);
                      }}
                      className="w-full px-3 py-2 text-left text-sm hover:bg-gray-50 text-gray-700"
                    >
                      Use "{countrySearch}"
                    </button>
                  ) : (
                    <div className="px-3 py-2 text-sm text-gray-500">Type to search...</div>
                  )}
                </div>
              )}
            </div>

            {/* Phone Field with independent prefix selector */}
            <div>
              <label className="flex items-center justify-between text-xs font-medium text-gray-700 mb-1">
                <span>Phone</span>
                <SavedIndicator field="phone" />
              </label>
              <div className="flex">
                {/* Phone prefix selector */}
                <div ref={prefixDropdownRef} className="relative">
                  <button
                    type="button"
                    onClick={() => setShowPrefixDropdown(!showPrefixDropdown)}
                    className="inline-flex items-center px-3 py-2 text-sm text-gray-700 bg-gray-100 border border-r-0 border-gray-300 rounded-l hover:bg-gray-200 min-w-[70px] justify-center"
                  >
                    {phonePrefix}
                    <span className="ml-1 text-[10px] text-gray-400">▼</span>
                  </button>
                  {/* Prefix dropdown */}
                  {showPrefixDropdown && (
                    <div className="absolute z-50 left-0 mt-1 w-64 bg-white border border-gray-200 rounded-lg shadow-lg max-h-48 overflow-hidden">
                      <div className="p-2 border-b border-gray-200">
                        <input
                          type="text"
                          value={prefixSearch}
                          onChange={(e) => setPrefixSearch(e.target.value)}
                          placeholder="Search..."
                          className="w-full px-2 py-1 text-sm border border-gray-300 rounded focus:outline-none focus:ring-1 focus:ring-blue-500"
                          autoFocus
                        />
                      </div>
                      <div className="max-h-36 overflow-y-auto">
                        {filteredPrefixes.map((c) => (
                          <button
                            key={c.code}
                            onClick={() => handlePrefixSelect(c.phonePrefix || '+1')}
                            className={`w-full px-3 py-2 text-left text-sm hover:bg-gray-50 flex items-center gap-2 ${
                              c.phonePrefix === phonePrefix ? 'bg-blue-50 text-blue-700' : 'text-gray-700'
                            }`}
                          >
                            <span className="font-medium min-w-[50px]">{c.phonePrefix}</span>
                            <span className="text-gray-500 truncate">{c.name}</span>
                          </button>
                        ))}
                      </div>
                    </div>
                  )}
                </div>
                <input
                  type="tel"
                  value={phone}
                  onChange={(e) => setPhone(e.target.value)}
                  onBlur={() => handleFieldSave('phone', phone)}
                  className="flex-1 px-3 py-2 text-sm border border-gray-300 rounded-r focus:outline-none focus:ring-2 focus:ring-blue-500"
                  placeholder="123456789"
                />
              </div>
            </div>

            {/* Privacy Checkbox */}
            <div className="flex items-start gap-2">
              <input
                type="checkbox"
                checked={privacyAccepted}
                onChange={(e) => setPrivacyAccepted(e.target.checked)}
                id="privacy-checkbox"
                className="mt-1 w-4 h-4 text-blue-600 border-gray-300 rounded focus:ring-blue-500"
              />
              <label htmlFor="privacy-checkbox" className="text-xs text-gray-600">
                I agree to the{' '}
                {privacyPolicyUrl ? (
                  <a href={privacyPolicyUrl} target="_blank" rel="noopener noreferrer" className="text-blue-600 hover:underline">
                    Privacy Policy
                  </a>
                ) : (
                  <span className="text-blue-600">Privacy Policy</span>
                )}
              </label>
            </div>
          </div>
        </div>

        {/* Divider - only show if research mode visible */}
        {showResearchMode && <div className="border-t border-gray-200"></div>}

        {/* Section 2: Research Mode - conditionally rendered */}
        {showResearchMode && (
        <div>
          <h3 className="text-sm font-semibold text-gray-900 mb-3 flex items-center gap-2">
            <span>🔬</span> Research Mode
          </h3>
          <div className="p-3 border border-gray-200 rounded">
            <div className="text-xs text-gray-600 mb-3">
              Extended AI thinking time (up to 15 minutes) for thorough, validated responses.
            </div>

            {/* Not verified - show message */}
            {!emailVerified && (
              <div className="p-3 bg-amber-50 border border-amber-200 rounded text-sm text-amber-800">
                <div className="font-medium mb-1">Email verification required</div>
                <div className="text-xs text-amber-700">
                  Verify your email address above to enable Research Mode.
                </div>
              </div>
            )}

            {/* Verified - show toggle and options */}
            {emailVerified && (
              <div className="space-y-3">
                <div className="flex items-center justify-between">
                  <span className="text-sm text-gray-700">Research Mode:</span>
                  <span className={`text-sm font-medium ${
                    researchModeState.enabled ? 'text-green-600' : 'text-gray-500'
                  }`}>
                    {researchModeState.enabled ? '● On' : '○ Off'}
                  </span>
                </div>

                <button
                  onClick={() => onToggleResearchMode(!researchModeState.enabled)}
                  className={`w-full px-4 py-2 text-sm font-medium rounded transition-colors ${
                    researchModeState.enabled
                      ? 'bg-red-100 text-red-700 hover:bg-red-200'
                      : 'bg-blue-100 text-blue-700 hover:bg-blue-200'
                  }`}
                >
                  {researchModeState.enabled ? 'Disable Research Mode' : 'Enable Research Mode'}
                </button>

                {/* Notification preferences */}
                {researchModeState.enabled && (
                  <div className="pt-2 border-t border-gray-200">
                    <div className="text-xs font-medium text-gray-700 mb-2">
                      Notify me when research complete:
                    </div>
                    <div className="space-y-1.5">
                      <label className="flex items-center gap-2 text-xs text-gray-600">
                        <input
                          type="checkbox"
                          checked={researchModeState.notificationPrefs.browser}
                          onChange={(e) => onUpdateNotificationPrefs({
                            ...researchModeState.notificationPrefs,
                            browser: e.target.checked,
                          })}
                          className="w-4 h-4 text-blue-600 border-gray-300 rounded focus:ring-blue-500"
                        />
                        Browser notification
                      </label>
                      <label className="flex items-center gap-2 text-xs text-gray-600">
                        <input
                          type="checkbox"
                          checked={researchModeState.notificationPrefs.email}
                          onChange={(e) => onUpdateNotificationPrefs({
                            ...researchModeState.notificationPrefs,
                            email: e.target.checked,
                          })}
                          className="w-4 h-4 text-blue-600 border-gray-300 rounded focus:ring-blue-500"
                        />
                        Email notification
                      </label>
                    </div>
                  </div>
                )}
              </div>
            )}
          </div>
        </div>
        )}
      </div>

      {/* Footer Links */}
      <div className="px-4 py-3 border-t border-gray-200 text-center">
        <div className="flex gap-4 justify-center text-xs text-gray-500">
          {privacyPolicyUrl && (
            <>
              <a href={privacyPolicyUrl} target="_blank" rel="noopener noreferrer" className="hover:text-blue-600">
                Privacy Policy
              </a>
              <span>•</span>
            </>
          )}
          <a href="#" className="hover:text-blue-600">Terms</a>
        </div>
      </div>
    </div>
  );
}
